<template>
  <TransitionRoot appear :show="showModal" as="template">
    <Dialog as="div" @close="closeModal">
      <div class="fixed inset-0 z-30 overflow-y-auto">
        <div class="min-h-screen text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-10"
            leave="duration-200 ease-in"
            leave-from="opacity-10"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black opacity-30" />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="ease-out duration-500"
            enter-from="translate-y-full"
            enter-to="translate-y-0"
            leave="ease-in duration-500"
            leave-from="translate-y-0"
            leave-to="translate-y-full"
          >
            <div
              class="inset-x-0 bottom-0 absolute w-full overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg py-5"
            >
              <div class="pb-5 px-4">
                <div
                  class="flex flex-col pb-2 border-b border-brand-light-gray"
                  @click="goToViewApp"
                >
                  <div class="flex items-center">
                    <h2 class="text-lg text-brand-black">
                      {{ app.applicant.name }}
                    </h2>
                  </div>
                  <p class="text-sm italic text-brand-dark-gray">
                    {{ app.unit_address }}
                  </p>
                  <p class="text-xs italic text-brand-mid-gray">
                    {{
                      getCurrentUser.landlord.in_company ? `sub. to ${app.landlord.name} - ` : ""
                    }}
                    {{ formatCreatedAt(app.created) }}
                  </p>

                  <template v-if="showViewAppLink">
                    <div class="pt-2 flex flex-row items-center">
                      <EyeIcon class="w-7 h-7 text-brand-green mr-2" />
                      <p>View application and reports</p>
                    </div>

                    <div class="py-1 flex flex-row">
                      <div class="flex items-center">
                        <div
                          class="py-1 px-1.5 rounded text-sm"
                          :class="[
                            app.reports.filter((r) => r.status === 'ready').length > 0
                              ? 'bg-brand-background-light-blue text-brand-blue'
                              : 'bg-brand-light-gray text-brand-mid-gray'
                          ]"
                        >
                          {{ app.reports.filter((r) => r.status === "ready").length }}/{{
                            app.reports.length
                          }}
                          reports ready
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

                <div class="flex flex-col">
                  <div
                    class="flex flex-col border-b border-brand-light-gray py-3 cursor-pointer"
                    @click="openShareModal"
                  >
                    <div class="flex flex-row">
                      <div class="flex items-center">
                        <PaperPlaneIcon :classes="'w-7 h-7 text-brand-green'" />
                      </div>
                      <div class="flex items-center pl-3">
                        <p class="text-base">Share</p>
                      </div>
                    </div>
                    <div class="flex flex-row py-3">
                      <div class="flex items-center">
                        <p class="text-xs">Shared with</p>
                      </div>
                      <div class="flex items-center ml-2">
                        <div class="-space-x-5 flex flex-row">
                          <template v-for="(share, index) in app.shares" :key="index">
                            <Gravatar
                              v-if="index < 3"
                              :photo="share.shared_to.profile_photo"
                              :zIndex="10 - index"
                              :displayName="formatGravatar(share)"
                              :color="`bg-palette-gravatar-${index + 1}`"
                            />
                          </template>
                        </div>
                        <p class="ml-1 mt-5" v-if="app.shares.length > 3">...</p>
                      </div>
                    </div>
                  </div>

                  <div
                    class="flex flex-row border-b border-brand-light-gray py-4 cursor-pointer"
                    @click="openHistoryModal"
                  >
                    <div class="flex items-center">
                      <ClockIcon class="w-8 h-7 text-brand-green" />
                    </div>
                    <div class="flex items-center pl-3">
                      <p class="text-base">App history</p>
                    </div>
                  </div>

                  <div
                    class="flex flex-row border-b border-brand-light-gray py-4 cursor-pointer"
                    @click="openNotesModal"
                  >
                    <div class="flex items-center">
                      <DocumentTextIcon class="w-8 h-7 text-brand-green" />
                    </div>
                    <div class="flex items-center pl-3">
                      <p class="text-base">Notes</p>
                    </div>
                  </div>

                  <div
                    class="flex flex-row border-b border-brand-light-gray py-4 cursor-pointer"
                    @click="archiveOrUnarchiveApplication"
                  >
                    <div class="flex items-center">
                      <ArchiveIcon class="w-8 h-7 text-brand-green" />
                    </div>
                    <div class="flex items-center pl-3">
                      <p class="text-base">
                        {{ app.is_archived ? "Unarchive" : "Archive" }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <ActionToast ref="mobileActionModalToast" />
</template>

<script>
import moment from "moment"
import { mapGetters } from "vuex"
import {
  APPLICATION_BUNDLE_ARCHIVE,
  APPLICATION_BUNDLE_UNARCHIVE,
  APPLICATION_BUNDLE_GET_LIST,
  APPLICATION_GET
} from "@/store/types"

import { TransitionRoot, TransitionChild, Dialog, DialogOverlay } from "@headlessui/vue"

import { ClockIcon, EyeIcon, ArchiveIcon, DocumentTextIcon } from "@heroicons/vue/outline"

export default {
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    ClockIcon,
    EyeIcon,
    ArchiveIcon,
    DocumentTextIcon
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    app: {
      type: Object
    },
    showViewAppLink: {
      type: Boolean,
      default: true
    }
  },
  emits: ["share", "history", "notes", "close"],
  computed: {
    ...mapGetters(["getCurrentUser", "isApplicationBundleLoading"]),
    origin() {
      return location.origin
    }
  },
  methods: {
    closeModal() {
      this.$emit("close")
    },
    openShareModal() {
      this.$emit("share", this.app)
    },
    openHistoryModal() {
      this.$emit("history")
    },
    openNotesModal() {
      this.$emit("notes")
    },
    formatCreatedAt(date) {
      return `${moment(date).format("h:mma")} CST`
    },
    formatGravatar(share) {
      if (share.shared_to.name) {
        if (share.shared_to.name.split(" ").length > 1) {
          return `${share.shared_to.name.split(" ")[0][0]}${share.shared_to.name.split(" ")[1][0]}`
        } else {
          return `${share.shared_to.name.substring(0, 2)}`
        }
      } else if (share.shared_to.display_email) {
        return share.shared_to.display_email.substring(0, 2)
      } else {
        return "N/A"
      }
    },
    goToViewApp() {
      this.$router.push({
        name: "dashboard_view_application_summary",
        params: { id: this.app.id }
      })
    },
    async archiveOrUnarchiveApplication() {
      if (this.isApplicationBundleLoading) return

      const slugs = [this.app.bundle.slug]

      const action = this.app.is_archived ? "unarchive" : "archive"

      if (this.app.is_archived) {
        await this.$store.dispatch(APPLICATION_BUNDLE_UNARCHIVE, { slugs })
      } else {
        await this.$store.dispatch(APPLICATION_BUNDLE_ARCHIVE, { slugs })
      }

      if (this.$route.name.includes("dashboard_applicants")) {
        const status = this.$route.name.split("dashboard_applicants_")[1]
        await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
          status
        })
      } else {
        const id = this.$route.params.id
        await this.$store.dispatch(APPLICATION_GET, { id })
      }

      this.$refs.mobileActionModalToast.show({
        type: "success",
        hasClose: true,
        content: `Applicants ${action}d`,
        icon: "check"
      })

      this.$emit("close")
    }
  }
}
</script>
